import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { useDropzone } from "react-dropzone";

import { Toast } from "components/toast";

import { FileArrowUpIcon } from "@phosphor-icons/react";

type DropzoneIconPropsT = {
  icon?: ReactNode;
};

const DropzoneIcon = ({ icon }: DropzoneIconPropsT) => {
  return (
    <span className="self-center">
      {icon ?? <FileArrowUpIcon aria-hidden={true} size={40} />}
    </span>
  );
};

type DropzoneMessagePropsT = PropsWithChildren<{}>;

const DropzoneMainMessage = ({ children }: DropzoneMessagePropsT) => {
  return <p className="font-bold">{children}</p>;
};

const DropzoneSecondaryMessage = ({ children }: DropzoneMessagePropsT) => {
  return <span className="text-sm text-secondaryText">{children}</span>;
};

type DropzonePropsT = PropsWithChildren<{
  onDropFiles: (acceptedFiles: File[]) => void;
  ariaLabel?: string;
  accept?: { [key: string]: readonly string[] };
  maxSize?: number;
  maxFiles?: number;
}>;

const formatAcceptedTypes = (accept: {
  [key: string]: readonly string[];
}): string => {
  const extensions = Object.values(accept)
    .flat()
    .map((ext) => ext.replace(/^\./, "").toUpperCase());

  if (extensions.length === 0) return "";
  if (extensions.length === 1) return extensions[0];

  const last = extensions.at(-1);

  return `${extensions.slice(0, -1).join(", ")} o ${last}`;
};

const Dropzone = ({
  ariaLabel,
  children,
  accept,
  maxSize,
  maxFiles,
  onDropFiles,
}: DropzonePropsT) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onDropFiles(acceptedFiles);
    },
    [onDropFiles],
  );

  const acceptRef = useRef(accept);
  acceptRef.current = accept;

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections,
  } = useDropzone({ onDrop, accept, maxSize, maxFiles });

  useEffect(() => {
    if (fileRejections.length > 0 && acceptRef.current) {
      Toast(
        "warning",
        `Sólo se permite archivos de tipo: ${formatAcceptedTypes(acceptRef.current)}`,
      );
    }
  }, [fileRejections]);

  let borderClass = "border-gray-300";

  if (isDragReject) borderClass = "border-red-500";
  else if (isDragActive) borderClass = "border-secondary-PCS001";

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className={`flex flex-col gap-1 justify-center h-full w-full rounded-lg border-2 border-dashed ${borderClass} p-4 cursor-pointer hover:border-secondary-PCS001 transition-colors bg-transparent`}
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col items-center">{children}</div>
    </button>
  );
};

Dropzone.Icon = DropzoneIcon;
Dropzone.MainMessage = DropzoneMainMessage;
Dropzone.SecondaryMessage = DropzoneSecondaryMessage;

export default Dropzone;
